import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NoDataFoundComponent } from 'app/core/components/no-data-found/no-data-found.component';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';
import { Subject, takeUntil } from 'rxjs';
import { TableService } from '../../tables.service';
import { TableHistoryEntry, TableHistoryPage } from './table-history.model';
import { TableHistoryChange, changesOf, formatRowId, operationLabel } from './table-history.util';

export interface TableHistoryDialogData {
    /** Physical table name, as `get-table-data` takes it. */
    tableName: string;
    displayName: string;
    /** Same gate as editing the table. The API enforces it again on every revert. */
    canRevert: boolean;
}

/** Change history of an internal table, with a per-entry revert. Mirrors the document history dialog. */
@Component({
    selector: 'app-table-history',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        TranslocoModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressBarModule,
        SaiDialogHeaderComponent,
        NoDataFoundComponent,
    ],
    templateUrl: './table-history.component.html',
    styleUrl: './table-history.component.scss',
})
export class TableHistoryComponent implements OnInit, OnDestroy {
    readonly data = inject<TableHistoryDialogData>(MAT_DIALOG_DATA);
    private readonly _dialogRef = inject(MatDialogRef<TableHistoryComponent, boolean>);
    private readonly _tables = inject(TableService);
    private readonly _alert = inject(CustomAlertService);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _transloco = inject(TranslocoService);
    private readonly _destroy$ = new Subject<void>();

    lang = 'en';
    loading = true;
    errorMessage: string | null = null;
    page: TableHistoryPage | null = null;

    /** Entry awaiting the user's confirmation. */
    confirming: TableHistoryEntry | null = null;
    reverting = false;
    /** True once a revert succeeded, so the opener can refresh on close. */
    changed = false;

    ngOnInit(): void {
        this._transloco.langChanges$.pipe(takeUntil(this._destroy$)).subscribe(lang => {
            this.lang = lang;
            this._cdr.markForCheck();
        });
        this.load();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    /** The page's own permission wins over the opener's, which was only a guess. */
    get canRevert(): boolean {
        return this.data.canRevert && (this.page?.canRevert ?? true);
    }

    load(): void {
        this.loading = true;
        this.errorMessage = null;
        this._cdr.markForCheck();
        this._tables
            .getTableHistory(this.data.tableName, 200)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: page => {
                    this.page = page;
                    this.loading = false;
                    this._cdr.markForCheck();
                },
                error: (err: HttpErrorResponse) => {
                    this.errorMessage = this.messageOf(err, 'Failed to load change history');
                    this.loading = false;
                    this._cdr.markForCheck();
                },
            });
    }

    label(entry: TableHistoryEntry): string {
        return operationLabel(entry.operation);
    }

    rowLabel(entry: TableHistoryEntry): string {
        return formatRowId(entry.rowId);
    }

    changes(entry: TableHistoryEntry): TableHistoryChange[] {
        return changesOf(entry);
    }

    isRevertEntry(entry: TableHistoryEntry): boolean {
        return entry.operation.startsWith('revert_');
    }

    /** The person whose chat or action it was. */
    actorLabel(entry: TableHistoryEntry): string {
        return entry.actor.name ?? `#${entry.actor.id}`;
    }

    agentLabel(entry: TableHistoryEntry): string {
        return entry.agent ? (entry.agent.name ?? `#${entry.agent.id}`) : '';
    }

    askRevert(entry: TableHistoryEntry): void {
        if (this.reverting) return;
        this.confirming = entry;
    }

    cancelRevert(): void {
        if (this.reverting) return;
        this.confirming = null;
    }

    confirmRevert(): void {
        const target = this.confirming;
        // `reverting` also blocks a double click while the request is in flight.
        if (!target || this.reverting) return;
        this.reverting = true;
        this._tables
            .revertTableHistory(this.data.tableName, target.id)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this.reverting = false;
                    this.confirming = null;
                    this.changed = true;
                    this._alert.successMessage('Change reverted successfully');
                    this.load();
                },
                error: (err: HttpErrorResponse) => {
                    this.reverting = false;
                    this.confirming = null;
                    this._alert.failedMessage(this.messageOf(err, 'Failed to revert this change'));
                    // A conflict means the row or the history moved on: show the real state.
                    if (err.status === 409) this.load();
                    this._cdr.markForCheck();
                },
            });
    }

    close(): void {
        this._dialogRef.close(this.changed);
    }

    private messageOf(err: HttpErrorResponse, fallback: string): string {
        return err?.error?.message || fallback;
    }
}
